import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ChatMessage,
  SessionMetadata,
  ToolExecution,
  Bot,
} from '../types/chat.types';
import * as chatApi from '../services/chatApiService';
import { streamResponse } from '../services/streamingService';
import { generateSessionId } from '../utils/generateSessionId';
import { getCurrentUserInfo } from '../services/authService';

const SESSION_STORAGE_KEY = 'chat-current-session-id';
const BOT_STORAGE_KEY_PREFIX = 'chat-selected-bot-';

function getBotStorageKey(userId: string): string {
  return `${BOT_STORAGE_KEY_PREFIX}${userId}`;
}

interface ChatContextType {
  // Sessions
  sessions: SessionMetadata[];
  currentSessionId: string | null;
  loadSessions: () => Promise<void>;
  createNewSession: () => void;
  switchSession: (sessionId: string) => Promise<void>;

  // Messages
  messages: ChatMessage[];
  loadMessages: (sessionId: string) => Promise<void>;

  // Streaming
  isStreaming: boolean;
  streamingMessage: string;
  toolExecutions: ToolExecution[];
  progressMessage: string;
  sendMessage: (message: string, botId: string) => Promise<void>;

  // Bot
  bots: Bot[];
  botsLoading: boolean;
  currentBotId: string;
  setCurrentBotId: (botId: string) => void;
  loadBots: () => Promise<void>;

  // Error handling
  error: string | null;
  clearError: () => void;
}

/** Sentinel value incremented on every session switch to detect stale streaming callbacks */
let streamGeneration = 0;

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<SessionMetadata[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [toolExecutions, setToolExecutions] = useState<ToolExecution[]>([]);
  const [progressMessage, setProgressMessage] = useState('');
  const [currentBotId, setCurrentBotIdState] = useState('');
  const [bots, setBots] = useState<Bot[]>([]);
  const [botsLoading, setBotsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track user ID for scoped localStorage
  const userIdRef = useRef<string>('');

  // Abort controller for cancelling in-flight streams when switching sessions
  const streamAbortRef = useRef<AbortController | null>(null);
  // Generation counter to ignore stale streaming callbacks
  const streamGenRef = useRef<number>(0);

  /**
   * Save currentSessionId to localStorage when it changes
   */
  useEffect(() => {
    if (currentSessionId) {
      localStorage.setItem(SESSION_STORAGE_KEY, currentSessionId);
    }
  }, [currentSessionId]);

  /**
   * Load messages for a session
   */
  const loadMessages = useCallback(async (sessionId: string) => {
    try {
      const response = await chatApi.getSessionMessages(sessionId);
      setMessages(response.messages);
      setStreamingMessage('');
      setToolExecutions([]);
      setProgressMessage('');
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  /**
   * Refresh the session list without changing the active session.
   * Used after sending a message to update sidebar metadata (title, message count).
   */
  const refreshSessionList = useCallback(async () => {
    if (!currentBotId) return;
    try {
      const response = await chatApi.listSessions(currentBotId);
      setSessions(response.sessions);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [currentBotId]);

  /**
   * Load all sessions from API, filtered by current bot.
   * Restores the previously active session from localStorage on initial load.
   */
  const loadSessions = useCallback(async () => {
    if (!currentBotId) return;

    try {
      const response = await chatApi.listSessions(currentBotId);
      setSessions(response.sessions);

      // Try to restore previous session from localStorage
      const savedSessionId = localStorage.getItem(SESSION_STORAGE_KEY);
      if (savedSessionId && response.sessions.some(s => s.sessionId === savedSessionId)) {
        setCurrentSessionId(savedSessionId);
        await loadMessages(savedSessionId);
      } else if (response.sessions.length > 0) {
        // Fall back to most recent session
        const firstSessionId = response.sessions[0].sessionId;
        setCurrentSessionId(firstSessionId);
        await loadMessages(firstSessionId);
      } else {
        // No sessions for this bot — clear state
        setCurrentSessionId(null);
        setMessages([]);
      }
    } catch (err) {
      setError((err as Error).message);
    }
  }, [currentBotId, loadMessages]);

  /**
   * Load bots the user has access to
   */
  const loadBots = useCallback(async () => {
    try {
      setBotsLoading(true);

      // Get user ID for scoped localStorage
      const userInfo = await getCurrentUserInfo();
      if (userInfo) {
        userIdRef.current = userInfo.userId;
      }

      const response = await chatApi.listBots();
      setBots(response.bots);

      if (response.bots.length > 0) {
        // Restore saved bot selection (scoped by user)
        const storageKey = getBotStorageKey(userIdRef.current);
        const savedBotId = localStorage.getItem(storageKey);
        const validBot = savedBotId && response.bots.find(b => b.botId === savedBotId);
        const selectedId = validBot ? savedBotId! : response.bots[0].botId;
        setCurrentBotIdState(selectedId);
        localStorage.setItem(storageKey, selectedId);
      }
      // When bots is empty, don't write to localStorage
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBotsLoading(false);
    }
  }, []);

  /**
   * Switch bot: persist selection, reset conversation state
   */
  const setCurrentBotId = useCallback((botId: string) => {
    setCurrentBotIdState(botId);

    // Persist to user-scoped localStorage
    if (userIdRef.current) {
      localStorage.setItem(getBotStorageKey(userIdRef.current), botId);
    }

    // Reset conversation state
    setCurrentSessionId(null);
    setMessages([]);
    setStreamingMessage('');
    setToolExecutions([]);
    setProgressMessage('');
    localStorage.removeItem(SESSION_STORAGE_KEY);
  }, []);

  /**
   * Load sessions when currentBotId changes
   */
  useEffect(() => {
    if (currentBotId && !botsLoading) {
      loadSessions();
    }
  }, [currentBotId, botsLoading, loadSessions]);

  /**
   * Create new session
   * Gated: no-op when bots are loading or user has no bot access
   */
  const createNewSession = useCallback(() => {
    if (botsLoading || bots.length === 0) return;

    // Abort any in-flight stream
    if (streamAbortRef.current) {
      streamAbortRef.current.abort();
      streamAbortRef.current = null;
    }
    streamGenRef.current = ++streamGeneration;

    const newSessionId = generateSessionId();
    setCurrentSessionId(newSessionId);
    setMessages([]);
    setIsStreaming(false);
    setStreamingMessage('');
    setToolExecutions([]);
    setProgressMessage('');
  }, [botsLoading, bots]);

  /**
   * Switch to a different session
   * Aborts any in-flight stream so its callbacks don't pollute the new session
   */
  const switchSession = useCallback(async (sessionId: string) => {
    try {
      // Abort any in-flight stream
      if (streamAbortRef.current) {
        streamAbortRef.current.abort();
        streamAbortRef.current = null;
      }
      // Bump generation so any lingering callbacks are ignored
      streamGenRef.current = ++streamGeneration;

      setIsStreaming(false);
      setStreamingMessage('');
      setToolExecutions([]);
      setProgressMessage('');
      setCurrentSessionId(sessionId);
      await loadMessages(sessionId);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [loadMessages]);

  /**
   * Send a message and stream the response
   * Blocked when bots are loading or user has no bot access
   */
  const sendMessage = useCallback(
    async (message: string, botId: string) => {
      if (botsLoading || bots.length === 0) {
        setError('No bot access. Please contact your administrator.');
        return;
      }

      if (!currentSessionId) {
        setError('No active session');
        return;
      }

      // Track user message timestamp for status updates
      const userMessageTimestamp = Date.now();

      // Create an AbortController for this stream so switchSession can cancel it
      const abortController = new AbortController();
      streamAbortRef.current = abortController;
      const gen = streamGenRef.current;

      // Guard: ignore callback if session was switched since this stream started
      const isStale = () => streamGenRef.current !== gen;

      try {
        setIsStreaming(true);
        setStreamingMessage('');
        setToolExecutions([]);
        setProgressMessage('');
        setError(null);

        // Add user message to UI immediately with 'sending' status
        const userMessage: ChatMessage = {
          role: 'user',
          content: message,
          timestamp: userMessageTimestamp,
          status: 'sending', // Optimistic update
        };
        setMessages((prev) => [...prev, userMessage]);

        // Call Chat API to get streaming endpoint details
        const response = await chatApi.sendMessage(
          currentSessionId,
          message,
          botId
        );

        // Mark user message as sent
        if (!isStale()) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.timestamp === userMessageTimestamp
                ? { ...msg, status: 'sent' }
                : msg
            )
          );
        }

        // Stream the response
        await streamResponse(
          response.streamingEndpoint,
          response.streamingPayload,
          {
            onProgress: (message) => {
              if (isStale()) return;
              setProgressMessage(message);
            },

            onToken: (content) => {
              if (isStale()) return;
              setProgressMessage('');
              setStreamingMessage((prev) => prev + content);
            },

            onToolStart: (tool, args) => {
              if (isStale()) return;
              console.log('[ChatContext] Tool started:', tool, args);
              const toolId = generateSessionId();
              setToolExecutions((prev) => {
                const newExecution: ToolExecution = {
                  id: toolId,
                  tool,
                  args,
                  status: 'running' as const,
                  timestamp: Date.now(),
                };
                const updated = [...prev, newExecution];
                console.log('[ChatContext] Tool executions updated:', updated);
                return updated;
              });
            },

            onToolResult: (tool, result) => {
              if (isStale()) return;
              console.log('[ChatContext] Tool completed:', tool, result);
              setToolExecutions((prev) => {
                const runningExecs = prev.filter(
                  (exec) => exec.tool === tool && exec.status === 'running'
                );
                if (runningExecs.length === 0) {
                  console.warn('[ChatContext] No running execution found for tool:', tool);
                  return prev;
                }

                const targetId = runningExecs[runningExecs.length - 1].id;
                const updated = prev.map((exec) =>
                  exec.id === targetId
                    ? { ...exec, result, status: 'completed' as const }
                    : exec
                );
                console.log('[ChatContext] Tool executions after completion:', updated);
                return updated;
              });
            },

            onWarning: (warningMessage) => {
              console.warn('Streaming warning:', warningMessage);
            },

            onDone: (fullText, responseId) => {
              if (isStale()) return;
              // Add assistant message to UI
              const assistantMessage: ChatMessage = {
                role: 'assistant',
                content: fullText,
                timestamp: Date.now(),
                responseId,
              };
              setMessages((prev) => [...prev, assistantMessage]);
              setStreamingMessage('');
              setProgressMessage('');
              setIsStreaming(false);

              // Refresh session list to update sidebar metadata (title, message count)
              // Use refreshSessionList (not loadSessions) to avoid overwriting the active session
              refreshSessionList();
            },

            onError: async (errorMessage) => {
              if (isStale()) return;
              const i18nKey = `errors.${errorMessage}`;
              const translated = t(i18nKey);
              const displayMessage = translated !== i18nKey ? translated : t('errors.stream_failed');
              setError(displayMessage);
              setIsStreaming(false);
              setStreamingMessage('');
              setProgressMessage('');

              // Mark user message as failed
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.timestamp === userMessageTimestamp
                    ? { ...msg, status: 'failed' }
                    : msg
                )
              );

              // Reconcile: refresh messages to get actual saved history
              if (currentSessionId) {
                try {
                  await loadMessages(currentSessionId);
                } catch (loadError) {
                  console.error('Failed to reload messages after error:', loadError);
                }
              }
            },
          },
          abortController.signal
        );
      } catch (err) {
        // Ignore abort errors from session switching
        if ((err as Error).name === 'AbortError') return;

        setError((err as Error).message);
        setIsStreaming(false);
        setStreamingMessage('');
        setProgressMessage('');

        // Mark user message as failed
        setMessages((prev) =>
          prev.map((msg) =>
            msg.timestamp === userMessageTimestamp
              ? { ...msg, status: 'failed' }
              : msg
          )
        );

        // Reconcile: refresh messages on API error
        if (currentSessionId) {
          try {
            await loadMessages(currentSessionId);
          } catch (loadError) {
            console.error('Failed to reload messages after error:', loadError);
          }
        }
      }
    },
    [currentSessionId, refreshSessionList, loadMessages, botsLoading, bots]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: ChatContextType = {
    sessions,
    currentSessionId,
    loadSessions,
    createNewSession,
    switchSession,
    messages,
    loadMessages,
    isStreaming,
    streamingMessage,
    toolExecutions,
    progressMessage,
    sendMessage,
    bots,
    botsLoading,
    currentBotId,
    setCurrentBotId,
    loadBots,
    error,
    clearError,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
}
